import React from "react";
import PortfolioPageShell from "../components/portfolio/PortfolioPageShell";

const IMPACT = [
  {
    label: "Latency",
    value: "84% lower end-to-end processing time on a high-volume agentic pipeline",
    shadow: "5px 5px 0 0 #FBBF24",
    accent: "#B45309",
  },
  {
    label: "Throughput",
    value: "3–5x tokens/sec per GPU after KV-cache reuse and continuous batching",
    shadow: "5px 5px 0 0 #A78BFA",
    accent: "#7E22CE",
  },
  {
    label: "Scale",
    value: "14L+ user signals integrated; multi-region event-driven backend in production",
    shadow: "5px 5px 0 0 #4ADE80",
    accent: "#15803D",
  },
];

const ROLES = [
  {
    company: "PwC India",
    title: "AI Systems Engineer",
    dates: "Jan 2025 — Present",
    location: "Gurgaon, Haryana",
    highlights: [
      "Architected a production agentic platform for enterprise AP Invoice Processing with specialised agents for invoice validation, vendor comms, contract analysis, and due diligence. Event-driven backend on Kafka + Redis Streams handles concurrent transactions with idempotent consumers, saga-style compensation, and bounded retries, cutting end-to-end processing time by 84%.",
      "Built the inference layer on vLLM and TensorRT-LLM with paged attention, continuous batching, and prefix caching; tuned KV-cache reuse and quantisation (FP8, AWQ, GPTQ) to hold p99 latency under SLA while pushing 3–5x throughput per GPU.",
      "Designed the data plane: hybrid retrieval over Postgres + Milvus with re-rankers, OCR ingestion at 92%+ accuracy, and a chunk-level provenance store so every model output resolves back to the source. Fine-tuned domain LLMs with LoRA and QLoRA on multi-GPU (DeepSpeed ZeRO-3, FSDP) for the slices the base model could not handle.",
      "Wired the agentic runtime over LangGraph and MCP with durable checkpoints, per-tool circuit breakers, schema-validated tool registries, and explicit cancellation. Built end-to-end eval pipelines (paired bootstrap, MDE, red-team suites) and guardrails (PII, prompt injection, toxicity, unsafe-tool) that run as CI gates on every model promotion.",
      "Owned the observability stack: OpenTelemetry traces end-to-end, Prometheus + Grafana SLO dashboards, structured logs with request correlation, and on-call runbooks so a slow request can be explained in minutes.",
    ],
    tech: [
      "LangGraph",
      "vLLM",
      "TensorRT-LLM",
      "MCP",
      "Kafka",
      "Redis",
      "Postgres",
      "Milvus",
      "Kubernetes",
      "FastAPI",
      "LoRA / QLoRA",
      "DeepSpeed",
      "OpenTelemetry",
    ],
    shadow: "5px 5px 0 0 #0369A1",
    accent: "#0369A1",
  },
  {
    company: "Cars24 Financial Services",
    title: "ML Engineer / Data Scientist",
    dates: "Sep 2024 — Jan 2025",
    location: "Gurgaon, Haryana",
    highlights: [
      "Surfaced appography signals from 14 lakh+ Android users that had been dormant in the Cars24 credit funnel, productionised the feature pipeline on PySpark + BigQuery, and integrated the features into Risk, Collections, and Propensity models. Model AUC lifted by 4%.",
      "Promoted features that had been used only as top-of-funnel rules into proper model inputs, ran offline/online consistency checks, and shipped behind feature flags. The improved targeting cut customer acquisition cost through better funnel efficiency.",
      "Owned the offline-to-online consistency layer: schema contracts between training and serving stores, drift monitoring, and back-tests on every model bump.",
    ],
    tech: ["Python", "PySpark", "BigQuery", "Airflow", "Feature Store", "Credit Risk"],
    shadow: "5px 5px 0 0 #F472B6",
    accent: "#BE185D",
  },
  {
    company: "FN Mathlogic",
    title: "ML / Risk Analyst",
    dates: "Jul 2023 — Sep 2024",
    location: "Gurgaon, Haryana",
    highlights: [
      "Built credit-risk models for NTC and ETC customer segments using bureau and UPI transaction data. Trained XGBoost and logistic-regression ensembles to predict creditworthiness and default probability, lifting acquisition targeting and cutting portfolio risk by 6%.",
      "Owned end-to-end regulatory compliance and model governance across large-scale financial datasets in SQL and Hadoop, maintaining model accuracy, auditability, and lineage through the full lifecycle.",
      "Wrote and maintained the training-to-deployment pipeline (data ingestion, feature engineering, model packaging, monitoring) so model refreshes were a one-command operation, not a quarterly project.",
    ],
    tech: ["XGBoost", "SQL", "Hadoop", "Python", "Model Governance"],
    shadow: "5px 5px 0 0 #B45309",
    accent: "#B45309",
  },
  {
    company: "Independent R&D / Open Source",
    title: "Founder, Melonai · OSS Maintainer",
    dates: "2024 — Present",
    location: "Remote",
    highlights: [
      "Maintain mcpolish (static linter for MCP servers), memnex (cross-channel agent memory over MCP), evalsig (statistical eval gates for LLM benchmarks), and CiteForge (production multimodal RAG). Combined: 100+ tests, SARIF output, CI/pre-commit integrations, and PyPI distribution.",
      "Driving the systems work that did not fit inside a single employer: inference benchmarking harnesses, MCP tool-design rules, eval statistical rigor, and document-grounded RAG for layout-heavy PDFs.",
      "Writing publicly about the boring parts of the LLM stack — caching, batching, eval design, and the operational discipline that makes agents survive production.",
    ],
    tech: ["Python", "MCP", "LangGraph", "Chroma", "SQLite FTS5", "FastAPI", "PyPI", "GitHub Actions"],
    shadow: "5px 5px 0 0 #4ADE80",
    accent: "#15803D",
  },
];

export default function PortfolioExperience() {
  return (
    <PortfolioPageShell
      tag="EXPERIENCE"
      title="WHERE I'VE WORKED"
      subtitle="Professional Journey"
      description="Production AI systems across enterprise consulting, fintech, and open-source. Each role pulled me deeper into LLM serving, distributed backends, agentic orchestration, and the engineering discipline that holds AI systems together at scale."
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-10">
        {IMPACT.map((i) => (
          <article
            key={i.label}
            className="bg-white border-[3px] border-[#0A0A0A] p-5"
            style={{ boxShadow: i.shadow }}
          >
            <p
              className="font-arcade text-xs uppercase tracking-widest mb-2"
              style={{ color: i.accent }}
            >
              {i.label}
            </p>
            <h3 className="font-pixel text-sm text-[#0A0A0A] leading-snug">
              {i.value}
            </h3>
          </article>
        ))}
      </div>

      <div className="space-y-6">
        {ROLES.map((r) => (
          <article
            key={r.company}
            className="bg-white border-[3px] border-[#0A0A0A] p-6 md:p-8 grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4 md:gap-8"
            style={{ boxShadow: r.shadow }}
          >
            <div className="font-terminal text-sm text-[#0A0A0A]/70">
              <p
                className="font-arcade text-base mb-1"
                style={{ color: r.accent }}
              >
                {r.dates}
              </p>
              <p>{r.location}</p>
            </div>
            <div>
              <h3 className="font-pixel text-sm md:text-base text-[#0A0A0A] leading-snug mb-2">
                {r.title}
              </h3>
              <h4 className="font-arcade text-base text-[#0A0A0A]/70 uppercase tracking-wider mb-5">
                {r.company}
              </h4>

              <ul className="space-y-3 mb-5">
                {r.highlights.map((h, i) => (
                  <li
                    key={i}
                    className="font-terminal text-sm md:text-base text-[#0A0A0A]/80 flex items-start gap-2 leading-relaxed"
                  >
                    <span
                      className="mt-0.5 shrink-0"
                      style={{ color: r.accent }}
                    >
                      &gt;
                    </span>
                    {h}
                  </li>
                ))}
              </ul>

              <div className="flex flex-wrap gap-2">
                {r.tech.map((t) => (
                  <span
                    key={t}
                    className="font-terminal text-xs text-[#0A0A0A] border border-[#0A0A0A] bg-[#F5F5F0] px-2 py-1"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </article>
        ))}
      </div>
    </PortfolioPageShell>
  );
}
